import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DollarSign, Users, TrendingUp, AlertCircle, Lock, Activity, LifeBuoy, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTr } from "@/i18n/translator";

export default function AdminMRR() {
  const tr = useTr();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ext, setExt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      setErr("Requiere permisos de administrador");
      return;
    }
    api.get("/admin/mrr").then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || "Error"));
    api.get("/admin/metrics/extended").then(({ data }) => setExt(data)).catch(() => {});
  }, [user]);

  if (err) {
    return (
      <div className="bg-white border border-[#0A0A0A] p-10 text-center" data-testid="admin-restricted">
        <Lock className="w-8 h-8 mx-auto text-[#E53935]" />
        <div className="font-mono-mark text-[10px] text-[#5C5C5C] mt-3">ACCESO DENEGADO</div>
        <div className="font-display text-xl mt-2">{err}</div>
      </div>
    );
  }

  if (!data) return <div className="font-mono-mark text-xs text-[#5C5C5C]">{tr("CARGANDO TELEMETRÍA…")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-mrr-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ADMIN · TELEMETRÍA FINANCIERA")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Panel operativo")}</h1>
      </div>

      {/* Money */}
      <div className="grid md:grid-cols-3 gap-0 border border-[#0A0A0A]">
        <HeroKpi label={tr("MRR")} value={`$${data.mrr.toLocaleString("en-US")}`} sub={tr("ingresos mensuales recurrentes")} icon={<DollarSign />} accent />
        <HeroKpi label="ARR" value={`$${data.arr.toLocaleString("en-US")}`} sub={tr("proyección anualizada")} icon={<TrendingUp />} />
        <HeroKpi label={tr("ARPU")} value={`$${data.arpu}`} sub={tr("ingreso promedio por usuario pagado")} icon={<Users />} />
      </div>

      {/* Users */}
      <div className="grid md:grid-cols-4 gap-0 border border-[#0A0A0A]">
        <SmallKpi label={tr("USUARIOS")} value={data.total_users} />
        <SmallKpi label={tr("PAGADOS")} value={data.paid_users} />
        <SmallKpi label={tr("GRATIS")} value={data.free_users} />
        <SmallKpi label={tr("% CONVERSIÓN")} value={(data.paid_ratio * 100).toFixed(1) + "%"} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Breakdown */}
        <div className="lg:col-span-2 bg-white border border-[#0A0A0A] p-6">
          <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-4">{tr("DESGLOSE POR PLAN")}</div>
          <table className="w-full text-sm font-mono-data">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="text-left px-3 py-2 font-mono-mark text-[10px]">{tr("PLAN")}</th>
                <th className="text-right px-3 py-2 font-mono-mark text-[10px]">{tr("USUARIOS")}</th>
                <th className="text-right px-3 py-2 font-mono-mark text-[10px]">{tr("MRR")}</th>
                <th className="text-right px-3 py-2 font-mono-mark text-[10px]">{tr("% DE MRR")}</th>
              </tr>
            </thead>
            <tbody>
              {data.breakdown.map((r) => (
                <tr key={r.plan} className="border-t border-[#0A0A0A]">
                  <td className="px-3 py-3 uppercase">{r.plan}</td>
                  <td className="text-right px-3 py-3">{r.users}</td>
                  <td className="text-right px-3 py-3">${r.mrr}</td>
                  <td className="text-right px-3 py-3">{data.mrr > 0 ? ((r.mrr / data.mrr) * 100).toFixed(1) + "%" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Funnel */}
        <div className="bg-white border border-[#0A0A0A] p-6 space-y-4">
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("EMBUDO · 7D")}</div>
          <Stat label={tr("NUEVOS USUARIOS")} value={data.new_users_7d} />
          <Stat label={tr("PAGOS PENDIENTES")} value={data.pending_transactions} warn />
          <Stat label="REFERIDOS TOTAL" value={data.total_referrals} />
          {data.pending_transactions > 5 && (
            <div className="border border-[#E53935] bg-[#E53935]/10 p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#E53935] shrink-0 mt-0.5" />
              <div className="font-mono-data text-xs">
                {tr("Muchos pagos pendientes. Revisa el flujo de checkout.")}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent paid */}
      <div className="bg-white border border-[#0A0A0A]">
        <div className="px-6 py-4 border-b border-[#0A0A0A] font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ÚLTIMOS PAGOS CONFIRMADOS")}</div>
        {data.recent_paid.length === 0 ? (
          <div className="p-10 text-center text-sm font-mono-data text-[#5C5C5C]">{tr("Sin pagos confirmados todavía.")}</div>
        ) : (
          <table className="w-full text-sm font-mono-data">
            <thead>
              <tr className="border-b border-[#0A0A0A]">
                <th className="text-left px-4 py-3 font-mono-mark text-[10px]">USUARIO</th>
                <th className="text-left px-4 py-3 font-mono-mark text-[10px]">{tr("PLAN")}</th>
                <th className="text-right px-4 py-3 font-mono-mark text-[10px]">MONTO</th>
                <th className="text-right px-4 py-3 font-mono-mark text-[10px]">FECHA</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_paid.map((t, i) => (
                <tr key={i} className="border-t border-[#0A0A0A]">
                  <td className="px-4 py-3 truncate max-w-[200px]">{t.user_email}</td>
                  <td className="px-4 py-3 uppercase">{t.plan_id} {t.billing_period === "annual" && "(ANUAL)"}</td>
                  <td className="text-right px-4 py-3">${t.amount}</td>
                  <td className="text-right px-4 py-3">{new Date(t.updated_at).toLocaleDateString("es-MX")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Métricas extendidas */}
      {ext && (
        <>
          <div className="grid md:grid-cols-4 gap-0 border border-[#0A0A0A]">
            <SmallKpi label={tr("NUEVOS 30D")} value={ext.new_users_30d} />
            <SmallKpi label={tr("ACTIVOS")} value={ext.active_users} />
            <SmallKpi label={tr("SUSPENDIDOS")} value={ext.inactive_users} />
            <SmallKpi label={tr("TICKETS ABIERTOS")} value={ext.open_tickets} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#0A0A0A] p-6">
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-4 flex items-center gap-2">
                <Activity className="w-3 h-3" /> {tr("ARPU POR PLAN")}
              </div>
              <table className="w-full text-sm font-mono-data">
                <thead className="bg-[#0A0A0A] text-white">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono-mark text-[10px]">PLAN</th>
                    <th className="text-right px-3 py-2 font-mono-mark text-[10px]">USUARIOS</th>
                    <th className="text-right px-3 py-2 font-mono-mark text-[10px]">PRECIO</th>
                    <th className="text-right px-3 py-2 font-mono-mark text-[10px]">MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {ext.arpu_by_plan.map((r) => (
                    <tr key={r.plan} className="border-t border-[#0A0A0A]">
                      <td className="px-3 py-3 uppercase">{r.plan}</td>
                      <td className="text-right px-3 py-3">{r.users}</td>
                      <td className="text-right px-3 py-3">${r.price}</td>
                      <td className="text-right px-3 py-3">${r.mrr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-[#0A0A0A] p-6 space-y-4">
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] flex items-center gap-2">
                <UserPlus className="w-3 h-3" /> {tr("ACTIVIDAD GLOBAL")}
              </div>
              <Stat label={tr("POSTS GENERADOS TOTAL")} value={ext.total_generations} />
              <Stat label={tr("IMÁGENES GENERADAS TOTAL")} value={ext.total_images} />
              <Stat label={tr("REFERIDOS APLICADOS")} value={ext.referrals_applied} />
              <Stat label={tr("PAGOS COMPLETADOS")} value={ext.paid_txs} />
              <Stat label={tr("PAGOS PENDIENTES")} value={ext.pending_txs} warn={ext.pending_txs > 5} />
              {ext.open_tickets > 0 && (
                <div className="border border-[#E53935] bg-[#E53935]/10 p-3 flex items-start gap-2">
                  <LifeBuoy className="w-4 h-4 text-[#E53935] shrink-0 mt-0.5" />
                  <div className="font-mono-data text-xs">
                    {ext.open_tickets} {tr("ticket(s) esperando respuesta.")} <a href="/dashboard/admin/support" className="underline decoration-[#E53935]">Ver soporte →</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function HeroKpi({ label, value, sub, icon, accent }) {
  return (
    <div className={`p-8 ${accent ? "bg-[#0A0A0A] text-white" : "bg-white border-l border-[#0A0A0A]"}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`font-mono-mark text-[10px] ${accent ? "text-[#FFD600]" : "text-[#5C5C5C]"}`}>{label}</span>
        <div className={`w-8 h-8 flex items-center justify-center ${accent ? "bg-[#E53935]" : "bg-[#0A0A0A] text-white"}`}>{icon}</div>
      </div>
      <div className="font-display text-5xl font-mono-data">{value}</div>
      <div className={`mt-2 text-xs ${accent ? "text-white/60" : "text-[#5C5C5C]"}`}>{sub}</div>
    </div>
  );
}

function SmallKpi({ label, value }) {
  return (
    <div className="p-5 bg-white border-l first:border-l-0 border-[#0A0A0A]">
      <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{label}</div>
      <div className="font-display text-3xl mt-1 font-mono-data">{value}</div>
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div className="flex items-baseline justify-between border-b border-[#0A0A0A] pb-2 last:border-b-0">
      <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{label}</span>
      <span className={`font-display text-2xl font-mono-data ${warn ? "text-[#E53935]" : ""}`}>{value}</span>
    </div>
  );
}
